import React, { FC, useMemo } from 'react';
import { BodyTextSmall, ImageButton, FlexCol, BasicContainer, FlexRow, H3Text, BodyText } from '@minecraft.atoms';
import { useTranslation } from '@blocs.i18n';
import { PlanTermEnum } from '@minecraft.graphql-operations';
import { TrustedBrands } from '@minecraft.molecules';
import Icon from '@minecraft.icon';
import { PlanExplorer } from './PlanExplorer';
import { AddPaymentProps } from './types';

type MobileRegistrationUpsellProps = {
  isAnnual: AddPaymentProps['isAnnual'];
  selectedPlan: AddPaymentProps['selectedPlan'];
  handleSetIsAnnual: () => void;
  price: string;
  disableTrial?: boolean;
};

export const MobileRegistrationUpsell: FC<MobileRegistrationUpsellProps> = React.memo(
  ({ isAnnual, selectedPlan, handleSetIsAnnual, price, disableTrial = false }) => {
    const { t } = useTranslation();

    const billingText = useMemo(() => {
      if (disableTrial) {
        return selectedPlan?.term.code === PlanTermEnum.Annual
          ? t('common:paywall.plan.withoutTrial.annual.price', { price })
          : t('common:paywall.plan.withoutTrial.m2m.price', { price });
      }
      return selectedPlan?.term.code === PlanTermEnum.Annual
        ? t('paywall.registration.billed_yearly', { price })
        : t('paywall.registration.billed_monthly', { price });
    }, [disableTrial, price, selectedPlan?.term.code, t]);

    return (
      <>
        <FlexRow mx="4" mb="2" gap="3" alignItems="center">
          <H3Text color="default">{t('common:paywall.paymentAndPlanDetails.title')}</H3Text>
        </FlexRow>
        <FlexRow w="fit" bgColor="vanillaPaywallDarkBg" borderRadius="3xl" mx="4">
          <ImageButton onClick={handleSetIsAnnual} unsetSizing>
            <BasicContainer
              bgColor={isAnnual ? 'paywallActiveToggle' : 'vanillaPaywallDarkBg'}
              borderRadius="3xl"
              className="cn_atom_px-3 cn_atom_py-1"
              data-testid="paywall-yearly-toggle"
            >
              <BodyText align="center" color={isAnnual ? 'inverted' : 'default'} fontWeight="medium">
                {t('common:planterm.titlecase.annual')}
              </BodyText>
            </BasicContainer>
          </ImageButton>
          <ImageButton onClick={handleSetIsAnnual} unsetSizing>
            <BasicContainer
              className="cn_atom_px-3 cn_atom_py-1"
              bgColor={!isAnnual ? 'paywallActiveToggle' : 'vanillaPaywallDarkBg'}
              borderRadius="3xl"
              data-testid="paywall-monthly-toggle"
            >
              <BodyText color={!isAnnual ? 'inverted' : 'default'} fontWeight="medium">
                {t('planterm.titlecase.monthly')}
              </BodyText>
            </BasicContainer>
          </ImageButton>
        </FlexRow>
        <BasicContainer w="full" className="cn_atom_pt-3">
          <FlexCol w="full" gap="4" justifyContent="flex-start" alignItems="flex-start" mx="4" className="cn_atom_pr-6">
            <FlexRow alignItems="baseline">
              {!disableTrial && (
                <BodyText fontWeight="bold" color="statusActive">
                  {t('common:paywall.paymentAndPlanDetails.upsellMessage', {
                    days: selectedPlan?.trialLengthDays ?? 0,
                  })}
                </BodyText>
              )}
              <BodyTextSmall className="cn_atom_pl-1" color="disabled">
                {billingText}
              </BodyTextSmall>
            </FlexRow>
            <FlexCol gap="2">
              <FlexRow w="full" gap="2" alignItems="center">
                <Icon name="roles-published" />
                <BodyTextSmall>{t('common:paywall.paymentAndPlanDetails.bullet1')}</BodyTextSmall>
              </FlexRow>
              <FlexRow w="full" gap="2" alignItems="center">
                <Icon name="premium-tools" />
                <BodyTextSmall>{t('common:paywall.paymentAndPlanDetails.bullet2')}</BodyTextSmall>
              </FlexRow>
            </FlexCol>
            <PlanExplorer selectedPlan={selectedPlan} />
            <BasicContainer w="full">
              <TrustedBrands align="left" showBrands={['hulu', 'netflix', 'prime', 'hbo']} />
            </BasicContainer>
          </FlexCol>
        </BasicContainer>
      </>
    );
  }
);
